/* global PRIVATE_MEDIA_HOST, API_URL */
/**
 * SPEC-010 Phase 2: CloudFront signed-cookie readiness.
 *
 * Segment/manifest requests to the PRIVATE video distribution must carry the
 * CloudFront-* cookies the playlist endpoint sets, which requires
 * credentialed XHR (withCredentials). Public hosts must NOT be credentialed —
 * their CORS config doesn't allow credentials and the requests would fail.
 */

// PRIVATE_MEDIA_HOST is env-driven (mirrors the backend's PRIVATE_MEDIA_URL:
// videos-dev.stanwinstonschool.com on dev, videos.stanwinstonschool.com in
// production). The pattern fallback keeps the cutover safe if the env var is
// missing from a build — a silently absent flag here would 403 every segment
// once manifests go plain-URL (see the GA4 measurement-id incident).
const FALLBACK_HOST_PATTERN = /^videos(-[\w-]+)?\.stanwinstonschool\.com$/

export const isPrivateVideoUrl = (url) => {
  if (!url) return false
  try {
    const { host } = new URL(url)
    if (typeof PRIVATE_MEDIA_HOST !== 'undefined' && PRIVATE_MEDIA_HOST)
      return host === PRIVATE_MEDIA_HOST
    return FALLBACK_HOST_PATTERN.test(host)
  } catch (_) {
    // Relative or malformed URLs never target the private distribution
    return false
  }
}

const apiHost = () => {
  try {
    return typeof API_URL !== 'undefined' && API_URL ? new URL(API_URL).host : null
  } catch (_) {
    return null
  }
}

// The playlist MANIFESTS are served by the API host (which also refreshes
// the signed cookies on every manifest response). A cross-origin XHR only
// stores/sends cookies when credentialed, so JW Player's manifest requests
// to the API must be credentialed too — otherwise the Set-Cookie refresh is
// silently discarded and later segment requests go out cookieless.
export const isCredentialedVideoUrl = (url) => {
  if (!url) return false
  if (isPrivateVideoUrl(url)) return true
  try {
    return new URL(url).host === apiHost()
  } catch (_) {
    return false
  }
}

// Mark private-host and API-host sources so JW Player performs credentialed
// requests for their manifests/segments (and flags the media element for
// Safari's native HLS path). Other hosts pass through untouched.
export const withPrivateVideoCredentials = (playlist = []) =>
  playlist.map((item) => {
    const sources = (item.sources || []).map((source) =>
      isCredentialedVideoUrl(source.file)
        ? { ...source, withCredentials: true }
        : source
    )
    return { ...item, sources }
  })
